import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-aspen-warm-ivory dark:bg-aspen-night-sage">
      <PageSEO
        title="Privacy Policy — Ask Aspen"
        description="Read the Ask Aspen privacy policy. Learn how we handle your data, SMS messaging terms, and your rights as a user of our AI task coaching service."
        canonicalPath="/privacy"
        breadcrumbName="Privacy Policy"
      />
      <SiteHeader />

      {/* Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-aspen-twilight-teal rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-aspen-deep-eucalyptus dark:text-aspen-pearl mb-6">
            Privacy Policy
          </h1>

          <div className="prose prose-lg max-w-none text-aspen-deep-eucalyptus dark:text-aspen-pearl">
            <p className="text-sm text-aspen-sage-teal dark:text-aspen-gentle-teal mb-8">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                SMS Messaging Terms and Disclosures
              </h2>

              <div className="bg-aspen-morning-mist dark:bg-aspen-moonlit-clay/20 p-6 rounded-lg mb-6">
                <p className="font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                  Important SMS Disclosure:
                </p>
                <p className="text-aspen-deep-eucalyptus dark:text-aspen-pearl leading-relaxed">
                  "No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
                  All other categories exclude text messaging originator opt-in data and consent; this information will
                  not be shared with any third parties."
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-3 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                SMS Service Details
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li><strong>Message Frequency:</strong> Message frequency varies based on your preferences and reminder settings. You may receive daily, weekly, or custom scheduled reminders.</li>
                <li><strong>Message & Data Rates:</strong> Message and data rates may apply according to your mobile carrier plan.</li>
                <li><strong>Opt-Out Instructions:</strong> Reply STOP to any SMS message to immediately unsubscribe from all SMS communications.</li>
                <li><strong>Help Instructions:</strong> Reply HELP to any SMS message for customer support and assistance.</li>
                <li><strong>Customer Support:</strong> For additional help, contact us at support@askaspen.com</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Consent and Opt-In
              </h3>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                By providing your phone number and checking the consent box, you explicitly agree to receive SMS messages
                from Ask Aspen. This consent is not required to use our services, and you can opt-out at any time by
                replying STOP to any message.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Information We Collect
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We collect information you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>Phone number for SMS reminders and authentication</li>
                <li>Task and reminder data you create in our application</li>
                <li>Account preferences and settings</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                How We Use Your Information
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>Provide task management and reminder services</li>
                <li>Send SMS reminders and notifications as requested</li>
                <li>Authenticate your account and ensure security</li>
                <li>Improve our services and user experience</li>
                <li>Respond to your comments, questions, and requests</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Information Sharing and Disclosure
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We do not sell, trade, or otherwise transfer your personal information to third parties for marketing purposes.
                We may share your information only in the following limited circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>With service providers who assist in delivering SMS messages (subject to strict confidentiality agreements)</li>
                <li>When required by law or to protect our rights and safety</li>
                <li>In connection with a business transfer or merger (with prior notice to users)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Data Security
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We implement appropriate technical and organizational measures to protect your personal information
                against unauthorized access, alteration, disclosure, or destruction. This includes encryption of
                sensitive data and regular security assessments.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Your Rights and Choices
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                <li>Access, update, or delete your personal information</li>
                <li>Opt-out of SMS communications at any time by replying STOP</li>
                <li>Request a copy of the personal information we hold about you</li>
                <li>Withdraw consent for SMS communications (this will not affect app functionality)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Contact Information
              </h2>
              <p className="mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-aspen-morning-mist dark:bg-aspen-moonlit-clay/20 p-4 rounded-lg">
                <p className="text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                  <strong>Ask Aspen - A Manatee Media Product</strong><br />
                  Website: <a href="https://manateemedia.io" className="text-aspen-sage-teal hover:underline" target="_blank" rel="noopener noreferrer">manateemedia.io</a><br />
                  Email: <a href="mailto:manateemediallc@gmail.com" className="text-aspen-sage-teal hover:underline">manateemediallc@gmail.com</a><br />
                  SMS Support: Reply HELP to any message<br />
                  Opt-out: Reply STOP to any message
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                Changes to This Policy
              </h2>
              <p className="text-aspen-deep-eucalyptus dark:text-aspen-pearl">
                We may update this Privacy Policy from time to time. We will notify you of any material changes
                by posting the new Privacy Policy on this page and, if you have provided your phone number,
                via SMS notification.
              </p>
            </section>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default PrivacyPolicy;